import { Warehouse, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ResourcePool {
  amount: number;
  icon?: string;
  description?: string;
  tags?: string[];
}

interface ManpowerBatch {
  role: string;
  count: number;
  icon?: string;
}

interface ResourceBarWidgetProps {
  resourcePools?: Record<string, ResourcePool> | null;
  manpowerRoster?: Record<string, ManpowerBatch> | null;
  // Legacy support
  resources?: Record<string, number> | null;
  resourceTypes?: string[] | null;
  compact?: boolean;
}

export function ResourceBarWidget({ resourcePools, manpowerRoster, resources, resourceTypes, compact }: ResourceBarWidgetProps) {
  const entries: { name: string; amount: number; icon: string }[] = [];

  if (resourcePools && Object.keys(resourcePools).length > 0) {
    for (const [name, pool] of Object.entries(resourcePools)) {
      const p = pool as any;
      entries.push({
        name,
        amount: typeof p?.amount === "number" ? p.amount : 0,
        icon: p?.icon || "📦",
      });
    }
  } else if (resources) {
    const types = resourceTypes || Object.keys(resources);
    for (const t of types) {
      entries.push({ name: t, amount: resources[t] ?? 0, icon: LEGACY_ICONS[t] || "📦" });
    }
  }

  // Manpower summary
  const roster = manpowerRoster || {};
  const totalPersonnel = Object.values(roster).reduce((s, b: any) => s + (typeof b?.count === "number" ? b.count : 0), 0);

  if (entries.length === 0 && totalPersonnel === 0) return null;

  const display = entries.slice(0, compact ? 3 : 5);

  if (compact) {
    return (
      <div className="flex items-center gap-1.5">
        {display.length > 0 && (
          <>
            <Warehouse className="w-3 h-3 text-muted-foreground" />
            {display.map((e) => (
              <span key={e.name} className="text-[10px] text-muted-foreground">
                {e.icon}{e.amount}
              </span>
            ))}
          </>
        )}
        {totalPersonnel > 0 && (
          <>
            <Users className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">
              {totalPersonnel}
            </span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {display.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <Warehouse className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">Resources</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {entries.map((e) => (
              <Badge key={e.name} variant="secondary" className="text-[10px] gap-1 px-1.5 py-0.5">
                {e.icon} {e.name}: {e.amount}
              </Badge>
            ))}
          </div>
        </>
      )}
      {totalPersonnel > 0 && (
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs font-medium text-foreground">Personnel: {totalPersonnel}</span>
        </div>
      )}
    </div>
  );
}

const LEGACY_ICONS: Record<string, string> = {
  troops: "⚔️",
  workers: "👷",
  materials: "🧱",
  intel: "🔍",
  influence: "👑",
};
